import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

/**
 * The walk through a **keyset**-paged list, held where each half belongs.
 *
 * Three of the operator's queues — reports, cases, the notification inbox — are
 * paged by opaque cursor rather than by offset: the services buy their speed by
 * not running a count query, and there is no `current_page` to ask for. That
 * makes the offset hook beside this one inapplicable, and it is why all three
 * kept the walk in component state, where **back and forward did nothing** and
 * a reload silently threw the operator back to the first page mid-triage.
 *
 * **The ordinal goes in the URL; the cursors go in the history entry.** A cursor
 * is a two-hundred-character opaque token and a walk of five is a kilobyte of
 * address — and worse, a cursor pasted by someone else is a page this reader
 * cannot count. `history.state` carries exactly as far as the entry it belongs
 * to, so Back and Forward restore the precise page that was on screen, while the
 * address stays short and never claims an ordinal it cannot show.
 *
 * **A cold arrival is corrected rather than honoured.** Opening `?page=4` in a
 * new tab has no stack behind it — the cursors for pages two and three were
 * never walked — so the list shows page one and the address is `replace`d to
 * match. Rendering page one under «صفحة ٤» is the failure this exists to
 * prevent; a keyset page is simply not a shareable location, and saying so by
 * correcting the URL is the honest form of that.
 */
export function useCursorPages(): {
  /** The cursor to send, or `null` on the first page. */
  cursor: string | null;
  /** One-based ordinal of the page in hand. */
  page: number;
  hasPrevious: boolean;
  toPrevious: () => void;
  toNext: (nextCursor: string) => void;
  /** Back to the first page — a new view of the list is a new walk. */
  reset: () => void;
} {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const state = location.state as { cursorStack?: string[] } | null;
  const cursorStack = Array.isArray(state?.cursorStack) ? state.cursorStack : [];

  const requested = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const page = cursorStack.length + 1;

  useEffect(() => {
    if (requested === page) return;

    const next = new URLSearchParams(searchParams);
    if (page <= 1) next.delete('page');
    else next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [requested, page, searchParams, setSearchParams]);

  function go(stack: string[]) {
    const next = new URLSearchParams(searchParams);
    if (stack.length === 0) next.delete('page');
    else next.set('page', String(stack.length + 1));

    navigate({ search: next.toString() }, { state: { cursorStack: stack } });
  }

  return {
    cursor: cursorStack.at(-1) ?? null,
    page,
    hasPrevious: cursorStack.length > 0,
    toPrevious: () => go(cursorStack.slice(0, -1)),
    toNext: (nextCursor: string) => go([...cursorStack, nextCursor]),
    reset: () => go([]),
  };
}
